import { ccSurchargeTypes, discountTypes } from './enums';

export const roundUp = (money) => {
  // to avoid float inaccurate like 0.1 * 0.2 = 0.020000000000000004
  const fixed = parseFloat(money).toFixed(12);

  return Math.round(fixed * 100) / 100;
};

export const getItemAmount = ({ unitPrice = 0, quantity = 0, amount = 0 }) => {
  if (!unitPrice && !quantity) {
    return roundUp(amount);
  }

  return roundUp(unitPrice * roundUp(quantity));
};

export const getTotal = (lineItems) => {
  if (!lineItems.length) return 0;

  return lineItems.reduce((total, item) => total + getItemAmount(item), 0);
};

const calculateTax = (amount, taxRate) => {
  return roundUp(amount * ((taxRate?.percentage || 0) / 100));
};

export const getDiscount = (amount, discount) => {
  if (!discount) {
    return 0;
  }

  const discountAmount = discount.amount ? Number(discount.amount) : 0;

  let result = String(discount.type) === discountTypes.percent
    ? (discountAmount / 100) * amount
    : discountAmount;

  result = roundUp(result);

  return Math.min(result, amount);
};

export const calculateSummary = ({
  lineItems,
  taxRate,
  discount,
}) => {
  const [taxableAmount, nonTaxableAmount] = lineItems.reduce(
    ([taxableAmt, nonTaxableAmt], lineItem) => {
      const taxableVal = taxableAmt + (lineItem.isTaxable ? getItemAmount(lineItem) : 0);
      const nonTaxableVal = nonTaxableAmt + (lineItem.isTaxable ? 0 : getItemAmount(lineItem));

      return [taxableVal, nonTaxableVal];
    },
    [0, 0],
  );

  const amountWithoutDiscountAndTax = taxableAmount + nonTaxableAmount;
  const discountAmount = getDiscount(amountWithoutDiscountAndTax, discount);

  let taxableAmountDiscount = 0;

  if (discount) {
    if (String(discount.type) === discountTypes.percent) {
      taxableAmountDiscount = getDiscount(taxableAmount, discount);
    } else {
      taxableAmountDiscount = Math.max(discountAmount - nonTaxableAmount, 0);
    }
  }

  const taxableAmountAfterDiscount = Math.max(taxableAmount - taxableAmountDiscount, 0);
  const taxAmount = calculateTax(taxableAmountAfterDiscount, taxRate);
  const totalAmount = amountWithoutDiscountAndTax - discountAmount + taxAmount;

  return {
    subtotalAmount: amountWithoutDiscountAndTax,
    discountAmount,
    taxAmount,
    totalAmount,
  };
};

export const calculateEntitySummary = ({
  lineItems,
  taxRate,
  discount,
  preCalculatedSummary,
}) => {
  const [requiredLineItems, optionalLineItems] = lineItems.reduce(
    ([required, optional], lineItem) => {
      (lineItem.optionalState ? optional : required).push(lineItem);
      return [required, optional];
    },
    [[], []],
  );

  const acceptedOptionalItems = optionalLineItems.filter((item) => item.optionalState?.isAccepted);

  const usePreCalculated = Object.keys(preCalculatedSummary || {}).length > 0;

  const {
    subtotalAmount,
    discountAmount,
    taxAmount,
    totalAmount,
  } = usePreCalculated
    ? preCalculatedSummary
    : calculateSummary({
      lineItems: [...requiredLineItems, ...acceptedOptionalItems],
      taxRate,
      discount,
    });

  return {
    requiredLineItems,
    optionalLineItems,
    requiredSubtotal: getTotal(requiredLineItems),
    optionalSubtotal: getTotal(acceptedOptionalItems),
    subtotalAmount,
    discountAmount,
    taxAmount,
    totalAmount,
  };
};

export const calculateCcSurcharge = (ccSurchargeSettings = null, totalAmount = 0) => {
  if (!ccSurchargeSettings || !ccSurchargeSettings.isEnabled || !ccSurchargeSettings.value) {
    return 0;
  }

  const surchargeValue = Number(ccSurchargeSettings.value) || 0;

  if (surchargeValue === 0) {
    return 0;
  }

  if (ccSurchargeSettings.type === ccSurchargeTypes.Percent.value) {
    return roundUp((surchargeValue / 100) * totalAmount);
  }

  return roundUp(surchargeValue);
};

// A percentage tip is on the total before tax, as the backend charges it (PaymentService.GetTipBaseAmount).
export const getTipBaseAmount = (totalAmount, taxAmount) => Math.max((totalAmount ?? 0) - (taxAmount ?? 0), 0);

/**
 * A tip only goes on a card, so with one the backend leaves at least the card minimum of the invoice owing for the
 * card to take it with (CreditApplication.LeaveCardMinimumOwing). Minimum: GBP 0.30, everything else 0.50.
 */
export const creditWithTip = (credit, amountDue, tipAmount, country) => {
  if (!(tipAmount > 0)) {
    return credit;
  }

  const minimum = country === 'GB' ? 0.3 : 0.5;
  // Floored to the cent; toFixed first so 0.29 * 100 isn't floored to 28.
  const coverable = Math.floor(Number((Math.max(0, amountDue - minimum) * 100).toFixed(6))) / 100;

  return Math.min(credit, coverable);
};
